import { apiClient } from './client';

// Alineado con aws-serverless/src/models/device.model.ts (F5).
// `apiKeyHash` no existe aquí a propósito: el backend nunca lo devuelve.

export const DEVICE_TYPES = [
  'GPS_TRACKER', 'THERMOGRAPH', 'SOIL_SENSOR', 'WEATHER_STATION', 'DRONE', 'CARRIER_API', 'RAIN_GAUGE',
] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export const DEVICE_CAPABILITIES = [
  'GPS', 'TEMPERATURE', 'HUMIDITY', 'SOIL_MOISTURE', 'SOIL_EC', 'SOIL_PH', 'VIDEO', 'SPRAY', 'RAINFALL',
] as const;
export type DeviceCapability = (typeof DEVICE_CAPABILITIES)[number];

export const DEVICE_STATUSES = ['ACTIVE', 'INACTIVE', 'REVOKED'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

export const DEVICE_BINDING_TYPES = ['SHIPMENT', 'MODULE', 'LOTE', 'NONE'] as const;
export type DeviceBindingType = (typeof DEVICE_BINDING_TYPES)[number];

export interface Device {
  deviceId:            string;
  tenantId:            string;
  name:                string;
  deviceType:          DeviceType;
  capabilities:        DeviceCapability[];
  bindingType:         DeviceBindingType;
  bindingId?:          string;
  apiKeyPrefix:        string;
  status:              DeviceStatus;
  lastSeenAt?:         string;
  lastReadingSummary?: string;
  ranchoId?:           string;
  notes?:              string;
  createdAt:           string;
  updatedAt?:          string;
}

export interface CreateDeviceInput {
  name:          string;
  deviceType:    DeviceType;
  capabilities:  DeviceCapability[];
  bindingType?:  DeviceBindingType;
  bindingId?:    string;
  notes?:        string;
}

export interface UpdateDeviceInput {
  name?:        string;
  bindingType?: DeviceBindingType;
  bindingId?:   string;
  status?:      DeviceStatus;
  notes?:       string;
}

/** `apiKey` viene en claro solo aquí y en la rotación — no hay forma de releerla. */
export interface DeviceWithKey { device: Device; apiKey: string }

export const fetchDevices = async (): Promise<Device[]> =>
  (await apiClient.get('/api/devices')).data.items;

export const createDevice = async (input: CreateDeviceInput): Promise<DeviceWithKey> =>
  (await apiClient.post('/api/devices', input)).data;

export const updateDevice = async (deviceId: string, input: UpdateDeviceInput): Promise<Device> =>
  (await apiClient.patch(`/api/devices/${deviceId}`, input)).data;

export const rotateDeviceKey = async (deviceId: string): Promise<DeviceWithKey> =>
  (await apiClient.post(`/api/devices/${deviceId}/rotate-key`)).data;

export interface SensorReading {
  recordedAt:       string;
  temperatureC?:    number;
  humidityPct?:     number;
  latitude?:        number;
  longitude?:       number;
  soilMoisturePct?: number;
  batteryPct?:      number;
}

export const fetchTelemetry = async (
  targetType: 'SHIPMENT' | 'MODULE' | 'LOTE',
  targetId: string,
): Promise<SensorReading[]> =>
  (await apiClient.get(`/api/telemetry/${targetType}/${targetId}`)).data.items;
